import * as THREE from 'three';
import type { AmbiencePreset } from '../../../core/config/theme';

// Sky/periphery life — clouds by day, aurora and shooting stars by night. All
// of it lives ABOVE and BEHIND the play corridor so approaching numbers keep a
// calm backdrop (readability is king for this audience). Every effect is a
// small self-contained module implementing AmbienceEffect; themes pick by name
// (see AmbiencePreset). Purely visual: nothing here touches game state.

export interface AmbienceEffect {
  object: THREE.Object3D;
  update(dt: number, motion: boolean): void;
  dispose(): void;
}

/** Soft white blob on a canvas — cloud sprite / aurora ribbon source. */
function radialTexture(stops: Array<[number, string]>, w = 256, h = 128): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  const grd = ctx.createRadialGradient(w / 2, h / 2, 4, w / 2, h / 2, Math.min(w, h) / 2);
  for (const [at, color] of stops) grd.addColorStop(at, color);
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, w, h);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

class Clouds implements AmbienceEffect {
  readonly object = new THREE.Group();
  private readonly sprites: Array<{ mesh: THREE.Mesh; speed: number }> = [];
  private readonly material: THREE.MeshBasicMaterial;
  private readonly geometry = new THREE.PlaneGeometry(26, 11);

  constructor() {
    const tex = radialTexture([
      [0, 'rgba(255,255,255,0.85)'],
      [0.55, 'rgba(255,255,255,0.35)'],
      [1, 'rgba(255,255,255,0)'],
    ]);
    // fog:false — ambience lives at the fog's far edge and would otherwise
    // dissolve into it. The camera looks down ~30°, so the visible sky band
    // sits just above the terrain's far rim: keep everything low (y ≲ 9).
    this.material = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      depthWrite: false,
      opacity: 0.55,
      fog: false,
    });
    for (let i = 0; i < 6; i++) {
      const mesh = new THREE.Mesh(this.geometry, this.material);
      mesh.position.set(-80 + Math.random() * 160, 2.5 + Math.random() * 5, -62 - Math.random() * 18);
      mesh.scale.setScalar(0.7 + Math.random() * 1.1);
      this.sprites.push({ mesh, speed: 0.4 + Math.random() * 0.5 });
      this.object.add(mesh);
    }
  }

  update(dt: number, motion: boolean): void {
    if (!motion) return;
    for (const s of this.sprites) {
      s.mesh.position.x += s.speed * dt;
      if (s.mesh.position.x > 100) s.mesh.position.x = -100;
    }
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.map?.dispose();
    this.material.dispose();
  }
}

class Aurora implements AmbienceEffect {
  readonly object: THREE.Mesh;
  private readonly material: THREE.MeshBasicMaterial;
  private t = Math.random() * 10;

  /** Full-width ribbon: green→teal curtain with waving column heights. */
  private static ribbonTexture(): THREE.CanvasTexture {
    const w = 512;
    const h = 128;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    for (let x = 0; x < w; x++) {
      // Curtain columns: height and brightness wave along the ribbon.
      const wave = 0.55 + 0.45 * Math.sin(x * 0.045) * Math.sin(x * 0.011 + 2);
      const grd = ctx.createLinearGradient(0, 0, 0, h);
      grd.addColorStop(0, 'rgba(0,0,0,0)');
      grd.addColorStop(0.35, `rgba(90,255,170,${(0.75 * wave).toFixed(3)})`);
      grd.addColorStop(0.7, `rgba(60,210,255,${(0.45 * wave).toFixed(3)})`);
      grd.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grd;
      ctx.fillRect(x, 0, 1, h);
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = THREE.RepeatWrapping;
    return tex;
  }

  constructor() {
    this.material = new THREE.MeshBasicMaterial({
      map: Aurora.ribbonTexture(),
      transparent: true,
      depthWrite: false,
      opacity: 0.7,
      side: THREE.DoubleSide,
      fog: false,
    });
    this.object = new THREE.Mesh(new THREE.PlaneGeometry(140, 14), this.material);
    this.object.position.set(0, 5, -85);
    this.object.rotation.x = 0.14;
  }

  update(dt: number, motion: boolean): void {
    this.t += dt;
    // The shimmer stays even under reduce-motion (no translation, just glow).
    this.material.opacity = 0.66 + Math.sin(this.t * 0.6) * 0.14;
    if (motion && this.material.map) this.material.map.offset.x += dt * 0.008;
  }

  dispose(): void {
    this.object.geometry.dispose();
    this.material.map?.dispose();
    this.material.dispose();
  }
}

class ShootingStars implements AmbienceEffect {
  readonly object = new THREE.Group();
  private readonly streaks: Array<{
    mesh: THREE.Mesh;
    velocity: THREE.Vector3;
    life: number;
    delay: number;
  }> = [];
  private readonly material: THREE.MeshBasicMaterial;
  private readonly geometry = new THREE.PlaneGeometry(7, 0.16);

  constructor() {
    this.material = new THREE.MeshBasicMaterial({
      color: 0xdff4ff,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      opacity: 0,
      side: THREE.DoubleSide,
      fog: false,
    });
    for (let i = 0; i < 3; i++) {
      // Each streak clones the material so opacities fade independently.
      const mesh = new THREE.Mesh(this.geometry, this.material.clone());
      mesh.visible = false;
      this.streaks.push({
        mesh,
        velocity: new THREE.Vector3(),
        life: 0,
        delay: 2 + Math.random() * 8,
      });
      this.object.add(mesh);
    }
  }

  update(dt: number, motion: boolean): void {
    if (!motion) return;
    for (const s of this.streaks) {
      if (s.life <= 0) {
        s.delay -= dt;
        if (s.delay > 0) continue;
        // Relaunch in the visible sky band, streaking mostly sideways.
        const dir = Math.random() < 0.5 ? -1 : 1;
        s.mesh.position.set(dir * (15 + Math.random() * 45), 4 + Math.random() * 5, -68 - Math.random() * 16);
        s.velocity.set(-dir * (26 + Math.random() * 14), -(2 + Math.random() * 2.5), 0);
        s.mesh.rotation.z = Math.atan2(s.velocity.y, s.velocity.x);
        s.life = 1.1;
        s.mesh.visible = true;
      }
      s.life -= dt;
      if (s.life <= 0) {
        s.mesh.visible = false;
        s.delay = 3 + Math.random() * 9;
        continue;
      }
      s.mesh.position.addScaledVector(s.velocity, dt);
      (s.mesh.material as THREE.MeshBasicMaterial).opacity = Math.sin(Math.min(s.life / 1.1, 1) * Math.PI) * 0.9;
    }
  }

  dispose(): void {
    this.geometry.dispose();
    for (const s of this.streaks) (s.mesh.material as THREE.Material).dispose();
    this.material.dispose();
  }
}

const FACTORIES: Record<AmbiencePreset['effects'][number], () => AmbienceEffect> = {
  clouds: () => new Clouds(),
  aurora: () => new Aurora(),
  shootingStars: () => new ShootingStars(),
};

export function createAmbience(preset: AmbiencePreset): AmbienceEffect[] {
  return preset.effects.map((name) => FACTORIES[name]());
}
